import {
  Modal,
  IconButton,
  Button,
  ButtonVariant,
  IconButtonVariant,
  IconButtonSize,
  Radio,
  RadioSize,
  Select,
  Show
} from "shared/components";
import { Cancel01Icon } from "@hugeicons-pro/core-stroke-rounded";
import { AsyncSelect } from "../../components/AsyncSelect/AsyncSelect";
import {
  loadPropertyOptions,
  loadUnitOptions
} from "../../../api/landlord/asyncUnitProperties";
import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import addressApi from "../../../api/landlord/address";
import { toast } from "react-toastify";
import { getErrorMessage } from "../../../utils/error";
import { useUnitsPropertiesDictionary } from "shared/hooks/use-units-properties";
import { useApplicationSummary } from "shared/hooks/use-application-summary";
import { invalidateApplicationQueries } from "shared/utils/invalidate-application-queries";
import { RentAmountData, SetRentAmountOptions } from "./SetRentAmountOptions";
import { PreviewApplicationRentByUnit } from "types/application";
import {
  Unit as UnitType,
  UnitTemplateOptionListResponse
} from "types/landlord/address";
import { cx } from "shared/utils";
import { useAuthContext } from "providers/Auth";
import { CriteriaRentType } from "shared/types";
import api from "api/landlord/address";

enum View {
  ChooseUnit = "ChooseUnit",
  SetRentOptions = "SetRentOptions"
}

export const MoveModal = ({
  onClose,
  applicationId,
  invalidateQueries = []
}: {
  onClose: () => void;
  applicationId: string;
  invalidateQueries?: unknown[];
}) => {
  const { pms, type, permissions } = useAuthContext();
  const homeSiteRentEnabled = pms.isManageAmerica || type.isHomeSiteRentEnabled;
  const canOverrideTemplate = Boolean(
    permissions.canOverrideApplicantScreeningTemplate
  );
  const { Unit, unit, Property } = useUnitsPropertiesDictionary();
  const queryClient = useQueryClient();
  const [unitError, setUnitError] = useState("");
  const [propertyError, setPropertyError] = useState("");
  const [selectedPropertyId, setSelectedPropertyId] = useState<
    number | undefined
  >();
  const [selectedUnitId, setSelectedUnitId] = useState<number | undefined>();
  const [selectedUnitName, setSelectedUnitName] = useState<
    string | undefined
  >();
  const [manualRentsInput, setManualRentsInput] = useState<RentAmountData>();
  const [rentChangeType, setRentChangeType] = useState<
    "use_current" | "use_listed" | "manual"
  >("use_current");
  const [templateChoice, setTemplateChoice] = useState<
    "keep_existing" | "use_new"
  >(canOverrideTemplate ? "use_new" : "keep_existing");
  const [selectedTemplateId, setSelectedTemplateId] = useState<
    string | undefined
  >();

  const [view, setView] = useState<View>(View.ChooseUnit);

  const { data: destinationUnitData } = useQuery<UnitType>({
    queryKey: ["UNIT_FOR_MOVE", selectedUnitId],
    queryFn: () => addressApi.getUnitById(String(selectedUnitId)),
    enabled: Boolean(selectedUnitId),
    meta: {
      onError: (err: unknown) => toast.error(getErrorMessage(err))
    }
  });

  const { data: destinationUnitTemplateOptions } =
    useQuery<UnitTemplateOptionListResponse>({
      queryKey: ["UNIT_TEMPLATE_OPTIONS_FOR_MOVE", selectedUnitId],
      queryFn: () => addressApi.getUnitTemplateOptions(String(selectedUnitId)),
      enabled: Boolean(selectedUnitId),
      meta: {
        onError: (err: unknown) => toast.error(getErrorMessage(err))
      }
    });

  const destinationDefaultTemplateId =
    destinationUnitData?.verification_template?.id;

  const unitTemplateOptions = useMemo(() => {
    const options: { id: string; name: string }[] = [];
    const defaultTemplate = destinationUnitData?.verification_template;
    if (defaultTemplate?.id)
      options.push({ id: defaultTemplate.id, name: defaultTemplate.name });
    destinationUnitTemplateOptions?.items?.forEach((opt) => {
      if (
        opt.verification_template_identifier &&
        opt.verification_template_identifier !== defaultTemplate?.id
      )
        options.push({
          id: opt.verification_template_identifier,
          name: opt.verification_template_name || opt.name
        });
    });
    return options;
  }, [destinationUnitData, destinationUnitTemplateOptions]);

  const seededUnitIdRef = useRef<number | undefined>(undefined);
  useEffect(() => {
    if (!selectedUnitId || !destinationDefaultTemplateId) return;
    if (seededUnitIdRef.current === selectedUnitId) return;
    seededUnitIdRef.current = selectedUnitId;
    setSelectedTemplateId(destinationDefaultTemplateId);
  }, [selectedUnitId, destinationDefaultTemplateId]);

  const { data: application } = useApplicationSummary(applicationId);

  const previewVerificationTemplateId =
    templateChoice === "use_new" ? selectedTemplateId : undefined;

  const { data: listedApplicationRentByUnit } =
    useQuery<PreviewApplicationRentByUnit>({
      queryKey: [
        "PREVIEW_APPLICATION_RENT_BY_UNIT",
        applicationId,
        selectedUnitId,
        previewVerificationTemplateId
      ],
      queryFn: () =>
        api.getPreviewApplicationRentByUnit(
          applicationId,
          selectedUnitId || 0,
          previewVerificationTemplateId
        ),
      enabled: Boolean(selectedUnitId && applicationId)
    });

  const handleClose = () => {
    setSelectedPropertyId(undefined);
    setSelectedUnitId(undefined);
    setManualRentsInput(undefined);
    setView(View.ChooseUnit);
    onClose();
    setRentChangeType("use_current");
    setTemplateChoice(canOverrideTemplate ? "use_new" : "keep_existing");
    setSelectedTemplateId(undefined);
    seededUnitIdRef.current = undefined;
  };

  const buildTemplateOptions = () => {
    if (templateChoice === "keep_existing")
      return { keep_existing_template: true };
    if (selectedTemplateId)
      return { verification_template_id: selectedTemplateId };
    return undefined;
  };

  const canSubmit =
    templateChoice === "keep_existing" || Boolean(selectedTemplateId);

  const { mutate, isPending } = useMutation({
    mutationFn: (
      rentData: {
        change_type: "use_current" | "use_listed" | "manual";
      } & RentAmountData
    ) =>
      addressApi.updateApplicationUnit(
        applicationId,
        Number(selectedUnitId),
        rentData,
        buildTemplateOptions()
      ),
    onSuccess: () => {
      toast.success("Application successfully updated");
      invalidateApplicationQueries(
        queryClient,
        applicationId,
        invalidateQueries
      );
      handleClose();
    },
    onError: (e) => {
      toast.error(getErrorMessage(e));
    }
  });

  const handleContinue = () => {
    if (view === View.ChooseUnit) setView(View.SetRentOptions);
  };

  const panelClassName = useMemo(() => {
    if (view === View.ChooseUnit) return "max-w-[600px]";

    const applicationCriteriaRentType =
      application?.verification_template?.criteria_rent_type ||
      CriteriaRentType.Both;
    const listedApplicationCriteriaRentType =
      listedApplicationRentByUnit?.criteria_rent_type || CriteriaRentType.Both;

    if (homeSiteRentEnabled)
      if (listedApplicationCriteriaRentType === CriteriaRentType.Both)
        return "max-w-[877px]";
      else if (applicationCriteriaRentType === CriteriaRentType.Both)
        return "max-w-[720px]";

    return "max-w-[697px]";
  }, [
    view,
    homeSiteRentEnabled,
    application?.verification_template?.criteria_rent_type,
    listedApplicationRentByUnit?.criteria_rent_type
  ]);

  const handleConfirm = () => {
    const rentData = {
      change_type: rentChangeType,
      ...manualRentsInput
    };

    if (homeSiteRentEnabled && listedApplicationRentByUnit) {
      rentData.home_rent_amount =
        listedApplicationRentByUnit.criteria_rent_type !== CriteriaRentType.Site
          ? manualRentsInput?.home_rent_amount
          : listedApplicationRentByUnit.home_rent_amount;
      rentData.site_rent_amount =
        listedApplicationRentByUnit.criteria_rent_type !== CriteriaRentType.Home
          ? manualRentsInput?.site_rent_amount
          : listedApplicationRentByUnit.site_rent_amount;
    }

    mutate(rentData);
  };

  useEffect(() => {
    if (
      application &&
      listedApplicationRentByUnit &&
      application.verification_template?.criteria_rent_type !==
        listedApplicationRentByUnit.criteria_rent_type
    )
      setRentChangeType("use_listed");
  }, [application, listedApplicationRentByUnit]);

  const currentApplicationTemplate = application?.verification_template;

  const templateSelectOptions = useMemo(() => {
    if (templateChoice === "keep_existing")
      return currentApplicationTemplate?.id
        ? [
            {
              value: currentApplicationTemplate.id,
              label: currentApplicationTemplate.name
            }
          ]
        : [];
    return unitTemplateOptions.map((option) => ({
      value: option.id,
      label: option.name
    }));
  }, [templateChoice, currentApplicationTemplate, unitTemplateOptions]);

  const templateSelectValue =
    templateChoice === "keep_existing"
      ? currentApplicationTemplate?.id
      : selectedTemplateId;

  return (
    <Modal
      opened={Boolean(applicationId)}
      onClose={handleClose}
      panelClassName={cx("w-full", panelClassName)}
    >
      <div className="p-6 border-b border-alpha-100 relative">
        <div className="flex flex-row justify-between">
          <p className="m-0 text-xl font-bold leading-8">
            <Show if={view === View.ChooseUnit}>
              Move application to another {unit}
            </Show>
            <Show if={view === View.SetRentOptions}>
              Set rent amount for application
            </Show>
          </p>
          <IconButton
            HugeIcon={Cancel01Icon}
            variant={IconButtonVariant.TEXT_BASE}
            size={IconButtonSize.LG}
            onClick={() => {
              handleClose();
            }}
          />
        </div>

        <Show if={view === View.ChooseUnit}>
          <p className="mb-0 mt-2 text-sm text-content-400 max-w-[calc(100%-40px)]">
            The application will be moved to the selected {unit}. It can use the
            original application template or a new one.
          </p>
        </Show>
      </div>

      <Show if={view === View.ChooseUnit}>
        <div className="py-8 px-6 flex flex-col gap-5 bg-background-900">
          <div className="flex items-start gap-4">
            <div className="w-full">
              <AsyncSelect
                id="property"
                label={Property}
                value={selectedPropertyId || ""}
                searchable={true}
                hideOptional
                // @ts-expect-error
                loadOptions={loadPropertyOptions}
                error={propertyError}
                options={[]}
                onChange={(e) => {
                  if (!Array.isArray(e)) {
                    setSelectedPropertyId(Number(e.target.value));
                    setPropertyError("");
                    setSelectedUnitId(undefined);
                    setSelectedTemplateId(undefined);
                  }
                }}
                additional={{
                  page: 1
                }}
              />
            </div>
            <div className="w-full">
              <AsyncSelect
                id="unit"
                label={Unit}
                key={selectedPropertyId}
                searchable={true}
                hideOptional
                value={selectedUnitId || ""}
                options={[]}
                // @ts-expect-error
                loadOptions={loadUnitOptions}
                error={unitError}
                additional={{
                  page: 1,
                  propertyId: selectedPropertyId,
                  disabledTooltip: `You can move the application to a ${unit} that accepts applications`
                }}
                onChange={(e) => {
                  if (!Array.isArray(e)) {
                    setSelectedUnitId(Number(e.target.value));
                    setUnitError("");
                    setSelectedUnitName(e.label);
                    setSelectedTemplateId(undefined);
                  }
                }}
              />
            </div>
          </div>

          <Show if={selectedUnitId}>
            <div className="border-t border-alpha-100" />

            <div className="flex flex-col gap-4">
              <div className="flex items-center gap-4">
                <Radio
                  id="template-choice-keep"
                  name="template-choice"
                  label="Keep existing application template"
                  size={RadioSize.LG}
                  checked={templateChoice === "keep_existing"}
                  onChange={() => setTemplateChoice("keep_existing")}
                />
                <Radio
                  id="template-choice-new"
                  name="template-choice"
                  label={`Use new ${unit} template`}
                  size={RadioSize.LG}
                  checked={templateChoice === "use_new"}
                  onChange={() => setTemplateChoice("use_new")}
                  disabled={!canOverrideTemplate}
                />
              </div>

              <div
                className={cx(
                  templateChoice === "keep_existing" &&
                    "pointer-events-none opacity-60"
                )}
              >
                <Select
                  id="move-modal-template"
                  label="Screening template"
                  hideOptional
                  value={templateSelectValue}
                  onChange={(e) => setSelectedTemplateId(e.target.value)}
                  options={templateSelectOptions}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  searchable={true}
                  isDisabled={!canOverrideTemplate}
                  formatOptionLabel={(option, { context }) => {
                    if (
                      context === "menu" &&
                      "value" in option &&
                      typeof option.label === "string"
                    )
                      return (
                        <div>
                          <span className="text-base font-medium text-content-1000">
                            {option.label}{" "}
                          </span>
                          <Show
                            if={option.value === destinationDefaultTemplateId}
                          >
                            <span className="text-base text-content-200">
                              ({Unit} default)
                            </span>
                          </Show>
                        </div>
                      );
                    return <span>{option.label}</span>;
                  }}
                />
              </div>
            </div>
          </Show>
        </div>
        <div className="flex justify-end px-6 py-4 border-t border-alpha-100 gap-2">
          <Button className="w-[103px]" onClick={onClose}>
            Back
          </Button>
          <Button
            className="w-[130px]"
            variant={ButtonVariant.PRIMARY}
            onClick={handleContinue}
            disabled={!selectedUnitId || !canSubmit}
          >
            Continue
          </Button>
        </div>
      </Show>
      <Show if={view === View.SetRentOptions && selectedUnitId}>
        <SetRentAmountOptions
          rentChangeType={rentChangeType}
          setRentChangeType={setRentChangeType}
          currentApplicationRents={{
            monthly_rent_amount: application?.monthly_rent_amount,
            rent_amount: application?.rent_amount,
            home_rent_amount: application?.home_rent_amount,
            site_rent_amount: application?.site_rent_amount,
            housing_assistance_voucher_amount:
              application?.housing_assistance_voucher_amount,
            pet_rent_amount: application?.pet_rent_amount
          }}
          unitName={selectedUnitName || ""}
          manualRent={manualRentsInput}
          setManualRent={setManualRentsInput}
          currentCriteriaRentType={
            application?.verification_template?.criteria_rent_type ||
            CriteriaRentType.Both
          }
          listedApplicationRentByUnit={listedApplicationRentByUnit}
        />

        <div className="flex justify-end gap-2 px-6 py-4 border-t border-alpha-100">
          <Button
            className="w-[125px]"
            onClick={() => setView(View.ChooseUnit)}
          >
            Back
          </Button>
          <Button
            className="w-[153px]"
            loading={isPending}
            variant={ButtonVariant.PRIMARY}
            disabled={
              !canSubmit ||
              (rentChangeType === "manual" &&
                (!homeSiteRentEnabled
                  ? manualRentsInput?.rent_amount?.cents === undefined
                  : (listedApplicationRentByUnit?.criteria_rent_type !==
                      CriteriaRentType.Site &&
                      manualRentsInput?.home_rent_amount?.cents ===
                        undefined) ||
                    (listedApplicationRentByUnit?.criteria_rent_type !==
                      CriteriaRentType.Home &&
                      manualRentsInput?.site_rent_amount?.cents === undefined)))
            }
            onClick={handleConfirm}
          >
            Confirm
          </Button>
        </div>
      </Show>
    </Modal>
  );
};
